import {messages} from '@smartmixin/i18n';
import {EnvelopeSimple, MagnifyingGlass} from '@phosphor-icons/react';
import {Box, InputAdornment, Typography} from '@mui/material';
import {visuallyHidden} from '@mui/utils';
import React, {useCallback, useEffect, useId, useState} from 'react';
import ReactGA from 'react-ga4';
import {useIntl} from 'react-intl';
import {useNavigate} from 'react-router';
import {Button, TextField} from '../../components/ui';
import {
  CATEGORY_MESSAGES,
  HELP_SEARCH_INPUT_ID,
  SUPPORT_EMAIL,
} from '../constants';
import {useArticleSearch} from '../hooks/useArticleSearch';
import {useHelpBasePath} from '../hooks/useHelpLocale';
import {isMacLike} from '../../lib/platform';
import {elevation} from '../../theme/tokens';

// Zero-result queries are the content backlog — log them once the user
// stops typing.
const ZERO_RESULT_DEBOUNCE_MS = 800;
const ZERO_RESULT_MIN_LENGTH = 3;
const MAX_RESULTS = 10;
const STATUS_DEBOUNCE_MS = 300;

type SearchBarProps = {
  variant?: 'default' | 'hero';
};

/**
 * The help search, as a WAI-ARIA combobox: focus stays in the input while
 * ArrowDown/ArrowUp move through the results (aria-activedescendant),
 * Enter opens the active one, Escape closes the list, and a polite status
 * says how many articles matched. The list is drawn under the input in the
 * page (not a portal at the end of <body>, which Tab could never reach).
 * With nothing found it offers a mail to support instead of a dead end.
 */
const SearchBar = ({variant = 'default'}: SearchBarProps) => {
  const {query, setQuery, results} = useArticleSearch();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const navigate = useNavigate();
  const {formatMessage} = useIntl();
  const helpBasePath = useHelpBasePath();
  const listId = useId();
  const optionId = (index: number) => `${listId}-option-${index}`;

  const isHero = variant === 'hero';
  const hasQuery = query.trim().length > 0;
  const visible = results.slice(0, MAX_RESULTS);
  const showPanel = open && hasQuery;
  const expanded = showPanel && visible.length > 0;

  const close = useCallback(() => {
    setOpen(false);
    setActive(-1);
  }, []);

  const handleSelect = useCallback(
    (slug: string) => {
      setQuery('');
      close();
      navigate(`${helpBasePath}/${slug}`);
    },
    [close, helpBasePath, navigate, setQuery],
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setQuery(value);
    setOpen(value.trim().length > 0);
    setActive(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        if (!hasQuery || visible.length === 0) return;
        event.preventDefault();
        setOpen(true);
        setActive((index) => Math.min(index + 1, visible.length - 1));
        break;
      case 'ArrowUp':
        if (!expanded) return;
        event.preventDefault();
        setActive((index) => Math.max(index - 1, 0));
        break;
      case 'Enter':
        if (expanded && active >= 0 && visible[active]) {
          event.preventDefault();
          handleSelect(visible[active].slug);
        }
        break;
      case 'Escape':
        if (showPanel) {
          event.preventDefault();
          close();
        }
        break;
      default:
    }
  };

  // Log searches that found nothing (debounced until typing settles)
  useEffect(() => {
    const term = query.trim();
    if (term.length < ZERO_RESULT_MIN_LENGTH || results.length > 0) {
      return undefined;
    }
    const timer = setTimeout(() => {
      ReactGA.gtag('event', 'help_search_zero_results', {search_term: term});
    }, ZERO_RESULT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, results.length]);

  const contactHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    formatMessage(messages.helpCenter.supportEmailSubjectTopic, {
      topic: query.trim(),
    }),
  )}`;
  const panelText = showPanel
    ? visible.length > 0
      ? formatMessage(messages.stations.searchResultsFound, {
          count: results.length,
        })
      : formatMessage(messages.stations.noSearchResults)
    : '';

  // The count is announced once typing pauses, not once per keystroke.
  const [statusText, setStatusText] = useState('');
  useEffect(() => {
    const timer = setTimeout(
      () => setStatusText(panelText),
      STATUS_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [panelText]);

  // Keep the active option in view as the arrows move through a long list.
  useEffect(() => {
    if (expanded && active >= 0) {
      document
        .getElementById(optionId(active))
        ?.scrollIntoView?.({block: 'nearest'});
    }
    // optionId is derived from listId, which is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, expanded]);

  return (
    <Box
      // Focus moving within (to the contact link) keeps the panel open.
      onBlur={(event: React.FocusEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          close();
        }
      }}
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: isHero ? 600 : 500,
        mb: isHero ? 0 : 3,
      }}>
      <TextField
        id={HELP_SEARCH_INPUT_ID}
        // Named by a real <label for>, hidden: the magnifier and the
        // placeholder already show what the box is for.
        label={formatMessage(messages.helpCenter.searchArticles)}
        hideLabel
        fullWidth
        placeholder={formatMessage(messages.helpCenter.searchArticles)}
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setOpen(hasQuery)}
        autoComplete="off"
        slotProps={{
          htmlInput: {
            role: 'combobox',
            'aria-autocomplete': 'list',
            'aria-expanded': expanded,
            'aria-controls': expanded ? listId : undefined,
            'aria-activedescendant':
              expanded && active >= 0 ? optionId(active) : undefined,
          },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Box
                  component="span"
                  aria-hidden
                  sx={{display: 'flex', color: 'text.secondary'}}>
                  <MagnifyingGlass size={16} />
                </Box>
              </InputAdornment>
            ),
            endAdornment: isHero ? (
              <InputAdornment position="end">
                <Box
                  component="kbd"
                  aria-hidden
                  sx={{
                    display: {xs: 'none', md: 'inline-flex'},
                    px: 0.75,
                    py: 0.25,
                    borderRadius: '4px',
                    border: 1,
                    borderColor: 'divider',
                    color: 'text.secondary',
                    typography: 'caption',
                    fontFamily: 'inherit',
                  }}>
                  {isMacLike() ? '⌘K' : 'Ctrl K'}
                </Box>
              </InputAdornment>
            ) : undefined,
          },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {bgcolor: 'background.paper'},
          ...(isHero && {'& .MuiOutlinedInput-input': {py: 1.25}}),
        }}
      />
      {/* The count, said once, politely. The panel's visible copy of it is
          hidden from assistive tech so it is not read twice. */}
      <Box role="status" sx={visuallyHidden}>
        {statusText}
      </Box>
      {showPanel && (
        <Box
          data-testid="help-search-results"
          // A press anywhere in the panel (an option, the contact button,
          // the scrollbar) keeps focus in the input; the click still lands.
          onMouseDown={(event) => event.preventDefault()}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 0.5,
            zIndex: (theme) => theme.zIndex.speedDial,
            maxHeight: 400,
            overflowY: 'auto',
            bgcolor: 'background.paper',
            // Floating elevation (spec: menus, popovers, map panels): the
            // control edge and the floating shadow, so the panel separates
            // from the page in dark mode too, where a shadow barely shows.
            border: 1,
            borderColor: 'border.control',
            borderRadius: 1,
            boxShadow: (theme) => elevation.floating[theme.palette.mode],
          }}>
          <Typography
            aria-hidden
            variant="caption"
            component="p"
            sx={{
              color: 'text.secondary',
              px: 1.5,
              py: 1,
              ...(visible.length > 0 && {
                borderBottom: 1,
                borderColor: 'divider',
              }),
            }}>
            {panelText}
          </Typography>
          {visible.length === 0 ? (
            // Never a dead end: offer direct contact when search finds nothing
            <Box sx={{px: 1.5, pb: 1.5}}>
              <Button
                size="small"
                variant="secondary"
                startIcon={<EnvelopeSimple aria-hidden />}
                href={contactHref}>
                {formatMessage(messages.helpCenter.contactSupport)}
              </Button>
            </Box>
          ) : (
            <Box
              component="ul"
              role="listbox"
              id={listId}
              aria-label={formatMessage(messages.helpCenter.searchArticles)}
              sx={{listStyle: 'none', m: 0, p: 0.5}}>
              {visible.map((result, index) => (
                <Box
                  component="li"
                  key={result.id}
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === active}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => handleSelect(result.slug)}
                  sx={{
                    px: 1.25,
                    py: 1,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    '&[aria-selected="true"]': {bgcolor: 'action.selected'},
                  }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 'fontWeightMedium',
                    }}>
                    {result.title}
                  </Typography>
                  <Typography variant="caption" sx={{color: 'text.secondary'}}>
                    {CATEGORY_MESSAGES[result.category]
                      ? formatMessage(CATEGORY_MESSAGES[result.category])
                      : result.category}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SearchBar;
